import { Outlet, createRootRoute } from "@tanstack/react-router";
import { useConvexAuth } from "convex/react";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { Screen } from "../components/Screen";
import { TabBar } from "../components/ui/tab-bar";
import { Skeleton } from "../components/ui/skeleton";
import { JoinScreen } from "../screens/JoinScreen";
import { SignInScreen } from "../screens/SignInScreen";
import { copy } from "../lib/copy";
import { useSession } from "../lib/session";

/**
 * The shell every screen renders into, and the gate in front of it.
 *
 * The gate lives here rather than above the router so the router keeps owning
 * the URL — an invite link is `/join?code=…`, and the join screen has to be
 * able to read it. Every route in this app is client-side only anyway.
 *
 * ErrorBoundary wraps the Outlet, not the whole shell: a screen that throws
 * leaves the tab bar standing, so you can navigate out of the broken one.
 */
export const Route = createRootRoute({
  component: RootLayout,
});

/**
 * Five states, in the order they resolve. `useConvexAuth` rather than Clerk's
 * `useAuth`, because what gates the app is not that Clerk has a session but
 * that Convex has accepted its token — in the gap between those two every
 * query comes back unauthenticated.
 */
function RootLayout() {
  const { isLoading, isAuthenticated } = useConvexAuth();
  const session = useSession();

  // A skeleton rather than a spinner, and the app's own name rather than a
  // screen title — which screen this becomes is what is not known yet.
  if (isLoading) return <Loading />;

  // No tab bar on either of these: there is nothing yet to navigate between.
  if (!isAuthenticated) return <SignInScreen />;

  // Signed in; `households.me` still in flight.
  if (session === undefined) return <Loading />;

  // Signed in, member of no household.
  if (session === null) return <JoinScreen />;

  return (
    <div className="flex h-full flex-col">
      <main className="min-h-0 flex-1">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
      <TabBar />
    </div>
  );
}

function Loading() {
  return (
    <Screen title={copy.app.name} tabBar={false}>
      <Skeleton rows={["h-14", "h-14", "h-14"]} />
    </Screen>
  );
}
